import { createHoppApp } from "@hoppscotch/common"
import { useDesktopSettings } from "@hoppscotch/common/composables/desktop-settings"
import { useSettingStatic } from "@hoppscotch/common/composables/settings"
import { resolvePressedKey } from "@hoppscotch/common/helpers/keybindings"
import { getKeyboardLayoutStrategy } from "@hoppscotch/common/helpers/keyboard-strategy"
import { getService } from "@hoppscotch/common/modules/dioc"
import { getKernelMode } from "@hoppscotch/kernel"
import { emit, listen } from "@tauri-apps/api/event"
import { nextTick, ref, watch } from "vue"

// Platform imports
import { def as webAuth } from "@app/platform/auth/web"

import { def as desktopAuth } from "@app/platform/auth/desktop"

// Cloud-specific
import { def as backendDef } from "./backend.platform"
import { def as organizationDef } from "./organization.platform"
import { def as syncDef } from "./sync.platform"

// Std platform
import { def as desktopInstance } from "@app/platform/instance/desktop"
import { def as webInstance } from "@app/platform/instance/web"
import { kernelIO } from "@hoppscotch/common/platform/std/kernel-io"
import { stdFooterItems } from "@hoppscotch/common/platform/std/ui/footerItem"
import { stdSupportOptionItems } from "@hoppscotch/common/platform/std/ui/supportOptionsItem"

// Std interceptors
import { AgentKernelInterceptorService } from "@hoppscotch/common/platform/std/kernel-interceptors/agent"
import { BrowserKernelInterceptorService } from "@hoppscotch/common/platform/std/kernel-interceptors/browser"
import { ExtensionKernelInterceptorService } from "@hoppscotch/common/platform/std/kernel-interceptors/extension"
import { NativeKernelInterceptorService } from "@hoppscotch/common/platform/std/kernel-interceptors/native"
import { ProxyKernelInterceptorService } from "@hoppscotch/common/platform/std/kernel-interceptors/proxy"

// Cloud only
import { OrganizationSwitcherService } from "@app/services/organization-switcher.service"
import { OrganizationService } from "@app/services/organization.service"
import { DocumentationSearcherService } from "@app/services/spotlight/searchers/documentation.searcher"

// Analytics and experiments
import {
  def as webAnalytics,
  addPosthogIgnoreClass as webIgnore,
} from "@app/platform/analytics/web"
import { def as webExperiments } from "@app/platform/experiments/web"

import {
  def as desktopAnalytics,
  addPosthogIgnoreClass as desktopIgnore,
} from "@app/platform/analytics/desktop"
import { def as desktopExperiments } from "@app/platform/experiments/desktop"
import { registerAIChatCopy } from "@app/platform/experiments/chat-copy"

import { sentryModule as sentryModuleDesktop } from "@app/platform/sentry/desktop"
import { sentryModule as sentryModuleWeb } from "@app/platform/sentry/web"

import {
  chatWithUsFooterMenuItem,
  chatWithUsSupportOptionMenuItem,
  crispModule,
} from "./crisp"
import OrganizationModule from "./organization"

import CustomEmbeds from "@app/components/CustomEmbeds.vue"
import Newsletter from "@app/components/Newsletter.vue"
import UserDeactivate from "@app/components/UserDeactivate.vue"
import UserDeletionSoleTeamOwnerInfo from "@app/components/UserDeletionSoleTeamOwnerInfo.vue"

import DesktopSettingsSection from "@hoppscotch/common/components/settings/Desktop.vue"
import { useDesktopZoomEffect } from "@hoppscotch/common/composables/desktop-zoom"

import { HeaderDownloadableLinksService } from "./services/headerDownloadableLinks.service"

import { isDefaultCloudInstance } from "@app/helpers/utils/cloud-for-orgs"
import { initializeFirebase } from "./firebase/init"

initializeFirebase()

const PLATFORM_CONFIG = {
  web: {
    auth: webAuth,
    instance: webInstance,
    experiments: webExperiments,
    analytics: webAnalytics,
    ignore: webIgnore,

    interceptors: [
      BrowserKernelInterceptorService,
      ProxyKernelInterceptorService,
      AgentKernelInterceptorService,
      ExtensionKernelInterceptorService,
    ],
    defaultInterceptor: "browser",

    modules: [
      ...(isDefaultCloudInstance ? [crispModule] : []),
      sentryModuleWeb,
      OrganizationModule,
    ] as any[],

    menuItems: [chatWithUsFooterMenuItem, ...stdFooterItems] as any[],
    supportItems: [
      chatWithUsSupportOptionMenuItem,
      ...stdSupportOptionItems,
    ] as any[],
    profileSections: [
      Newsletter,
      ...(isDefaultCloudInstance ? [] : [UserDeactivate]),
    ] as any[],
    settingsSections: [] as any[],
    userDeletionInfo: UserDeletionSoleTeamOwnerInfo,
    embedsComponent: CustomEmbeds,
    organization: organizationDef,

    cookiesEnabled: false,
    setupServices: () => {
      if (!isDefaultCloudInstance) {
        getService(OrganizationService)
      }
      // Initialize organization switcher service for all cloud instances
      getService(OrganizationSwitcherService)
    },
  },

  desktop: {
    auth: desktopAuth,
    instance: desktopInstance,
    experiments: desktopExperiments,
    analytics: desktopAnalytics,
    ignore: desktopIgnore,

    interceptors: [
      NativeKernelInterceptorService,
      ProxyKernelInterceptorService,
    ],
    defaultInterceptor: "native",

    modules: [sentryModuleDesktop, OrganizationModule] as any[],
    menuItems: [...stdFooterItems] as any[],
    supportItems: [...stdSupportOptionItems] as any[],
    profileSections: [] as any[],
    settingsSections: [DesktopSettingsSection] as any[],
    userDeletionInfo: undefined,
    embedsComponent: undefined,
    organization: organizationDef,

    cookiesEnabled: true,
    setupServices: () => {
      // Initialize organization services for desktop
      getService(OrganizationService)
      getService(OrganizationSwitcherService)
    },
  },
}

const headerPaddingLeft = ref("0px")
const headerPaddingTop = ref("0px")

function setupDesktopUI() {
  // Hydrate the keyboard-layout-strategy holder at desktop bootstrap.
  // The composable's first call triggers loadInitial, which reads the
  // persisted strategy from tauri-plugin-store and writes it into the
  // shared holder. Without this call the holder stays at its module-
  // level default ("hybrid") until Desktop.vue mounts, so a persisted
  // "key" or "code" choice would be dormant on every restart until the
  // user opened the settings page.
  useDesktopSettings()

  headerPaddingTop.value = "0px"
  headerPaddingLeft.value = "80px"

  listen("will-enter-fullscreen", () => {
    headerPaddingTop.value = "0px"
    headerPaddingLeft.value = "0px"
  })

  listen("will-exit-fullscreen", () => {
    headerPaddingTop.value = "0px"
    headerPaddingLeft.value = "80px"
  })

  window.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Backspace" && !isTextInput(e.target)) {
        e.preventDefault()
      }
    },
    true
  )

  watch(
    useSettingStatic("BG_COLOR")[0],
    async () => {
      await nextTick()
      await emit("hopp-bg-changed")
    },
    { immediate: true }
  )
}

function isTextInput(target: EventTarget | null): boolean {
  if (target instanceof HTMLInputElement) {
    const textTypes = [
      "text",
      "email",
      "password",
      "number",
      "search",
      "tel",
      "url",
      "textarea",
    ]
    return textTypes.includes(target.type)
  }

  return (
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  )
}

async function initApp() {
  const platform = getKernelMode()
  const config = PLATFORM_CONFIG[platform]

  if (platform === "desktop") {
    setupDesktopUI()
    // Apply the persisted zoom to this window and keep it in sync with
    // the store. The launcher window runs its own copy of this watcher
    // from `hoppscotch-desktop/src/main.ts`, and the store is the shared
    // source of truth between the two.
    useDesktopZoomEffect()
  }

  await createHoppApp("#app", {
    ui: {
      additionalFooterMenuItems: config.menuItems,
      additionalSupportOptionsMenuItems: config.supportItems,
      onCodemirrorInstanceMount: config.ignore,
      additionalProfileSections: config.profileSections,
      additionalSettingsSections: config.settingsSections,
      appHeader: {
        paddingLeft: headerPaddingLeft,
        paddingTop: headerPaddingTop,
      },
      additionalUserDeletionSoleTeamOwnerInfo: config.userDeletionInfo,
      additionalEmbedsComponent: config.embedsComponent,
    },

    addedHoppModules: config.modules,

    analytics: config.analytics,
    auth: config.auth,
    kernelIO,
    instance: config.instance,

    kernelInterceptors: {
      default: config.defaultInterceptor,
      interceptors: config.interceptors.map((service) => ({
        type: "service" as const,
        service,
      })),
    },

    limits: {
      collectionImportSizeLimit: 50,
    },

    spotlight: {
      additionalSearchers: [DocumentationSearcherService],
    },

    platformFeatureFlags: {
      exportAsGIST: true,
      hasTelemetry: true,
      cookiesEnabled: config.cookiesEnabled,
      promptAsUsingCookies: false,
      workspaceSwitcherLogin: ref(false),
    },

    experiments: config.experiments,
    backend: backendDef,
    organization: config.organization,
    sync: syncDef,

    additionalInspectors: [
      { type: "service", service: ExtensionKernelInterceptorService },
    ],
    additionalLinks: [HeaderDownloadableLinksService],
  })

  // After boot so the `en` merge lands; the chat only exists on org instances.
  if (!isDefaultCloudInstance) registerAIChatCopy()

  // TODO: Specify via `addedServices` field above
  if (platform === "web" && !isDefaultCloudInstance) {
    getService(OrganizationService)
  }

  if (platform === "desktop") {
    const ALLOWED_DROP_SELECTORS = [
      '[draggable="true"]',
      ".draggable-content",
      ".draggable-handle",
      ".sortable-ghost",
      ".sortable-drag",
      ".sortable-chosen",
      ".vue-draggable",

      'input[type="file"]',
      'label[for*="attachment"]',
      ".file-chips-container",
      ".file-chips-wrapper",

      ".cm-editor",
      ".cm-content",
      ".cm-scroller",
      ".ace_editor",

      "[data-allow-drop]",
      ".drop-zone",

      "[ondrop]",
      "[data-drop-handler]",
    ].join(", ")

    const isAllowedDropTarget = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) {
        return false
      }

      if (target.closest(ALLOWED_DROP_SELECTORS)) {
        return true
      }

      const element = target as any
      if (element._vei?.onDrop || element.__vueListeners__?.drop) {
        return true
      }

      return false
    }

    document.addEventListener(
      "drop",
      (e) => {
        if (!isAllowedDropTarget(e.target)) {
          e.preventDefault()
          e.stopPropagation()
        }
      },
      false
    )

    document.addEventListener(
      "dragover",
      (e) => {
        e.preventDefault()
      },
      false
    )

    document.addEventListener(
      "dragstart",
      (e) => {
        if (!e.target || !(e.target instanceof HTMLElement)) {
          return
        }

        const target = e.target as HTMLElement
        if (
          !target.draggable &&
          !target.closest('[draggable="true"], .draggable-content')
        ) {
          e.preventDefault()
        }
      },
      false
    )

    window.addEventListener(
      "keydown",
      function (e) {
        // Prevent backspace navigation
        // NOTE: Only for "non-text" inputs
        if (e.key === "Backspace" && !isTextInput(e.target)) {
          e.preventDefault()
          return
        }

        // Skip during IME composition (CJK input). Modern browsers report
        // `isComposing`. Older ones use the sentinel `keyCode === 229`.
        if (e.isComposing || e.keyCode === 229) return

        // Skip when AltGr is the modifier. Browsers report AltGr as
        // Ctrl+Alt on Windows, so QWERTZ users typing `[` via AltGr+8
        // would otherwise match Ctrl+Alt+[ and get hijacked into the
        // MRU tab shortcut. `getModifierState("AltGraph")` is true only
        // for AltGr, not for genuine Ctrl+Alt.
        if (e.getModifierState("AltGraph")) return

        const isCtrlOrCmd = e.ctrlKey || e.metaKey
        if (!isCtrlOrCmd) return

        // Resolve the pressed key through the active layout strategy so
        // AZERTY's "A" keycap (physical KeyQ position) fires Ctrl+A,
        // not Ctrl+Q. The in-page handler uses the same resolver, so
        // routing the capture phase through it keeps both paths
        // consistent and lets the user's strategy choice take effect
        // everywhere, including for the desktop-shell shortcuts the
        // capture phase pre-empts.
        const key = resolvePressedKey(e, getKeyboardLayoutStrategy())
        if (!key) return

        let shortcutEvent: string | null = null

        if (!e.shiftKey && !e.altKey && key === "q") {
          // Ctrl/Cmd + Q - Quit Application
          shortcutEvent = "ctrl-q"
        } else if (!e.shiftKey && !e.altKey && key === "t") {
          // Ctrl/Cmd + T - New Tab
          shortcutEvent = "ctrl-t"
        } else if (!e.shiftKey && !e.altKey && key === "w") {
          // Ctrl/Cmd + W - Close Tab
          shortcutEvent = "ctrl-w"
        } else if (e.shiftKey && !e.altKey && key === "t") {
          // Ctrl/Cmd + Shift + T - Reopen Tab
          shortcutEvent = "ctrl-shift-t"
        } else if (!e.shiftKey && e.altKey && key === "right") {
          // Ctrl/Cmd + Alt + Right - Next Tab
          shortcutEvent = "ctrl-alt-right"
        } else if (!e.shiftKey && e.altKey && key === "left") {
          // Ctrl/Cmd + Alt + Left - Previous Tab
          shortcutEvent = "ctrl-alt-left"
        } else if (!e.shiftKey && e.altKey && key === "9") {
          // Ctrl/Cmd + Alt + 9 - First Tab
          shortcutEvent = "ctrl-alt-9"
        } else if (!e.shiftKey && e.altKey && key === "0") {
          // Ctrl/Cmd + Alt + 0 - Last Tab
          shortcutEvent = "ctrl-alt-0"
        } else if (!e.shiftKey && e.altKey && key === "u") {
          // Ctrl/Cmd + Alt + U - Focus URL Bar
          shortcutEvent = "ctrl-alt-u"
        } else if (!e.shiftKey && e.altKey && key === "]") {
          // Ctrl/Cmd + Alt + ] - MRU Tab Switch
          shortcutEvent = "ctrl-alt-]"
        } else if (!e.shiftKey && e.altKey && key === "[") {
          // Ctrl/Cmd + Alt + [ - MRU Tab Switch (Reverse)
          shortcutEvent = "ctrl-alt-["
        }

        if (shortcutEvent) {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()

          setTimeout(() => {
            emit("hoppscotch_desktop_shortcut", shortcutEvent).catch(
              (error) => {
                console.error("Failed to emit shortcut event:", error)
              }
            )
          }, 0)
        }
      },
      true
    )
  }
}

initApp().catch(console.error)
